// /apply/:eventId — the promoter's shareable "Apply to exhibit" link (see
// utils/applyLink.js). Resolves the event id to its current slug and hands
// over to the event page, which shows the apply banner for `?apply=1`.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PuffLoader } from 'react-spinners';

import api from '../../utils/axiosConfig';
import { APPLY_LANGS, APPLY_SOURCES } from 'utils/applyLink';

const ApplyRedirect = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const src = searchParams.get('src');
    const lang = searchParams.get('lang');

    api
      .get(`/events/${encodeURIComponent(eventId)}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.slug) {
          setNotFound(true);
          return;
        }
        const next = new URLSearchParams({ apply: '1' });
        if (APPLY_SOURCES.includes(src)) next.set('src', src);
        if (APPLY_LANGS.includes(lang)) {
          next.set('lng', lang);
          // i18n only reads ?lng= on a full page load; this is a client-side
          // hop. A language the visitor chose themselves still wins
          // (hooks/useLanguage).
          if (i18n.language !== lang) i18n.changeLanguage(lang);
        }
        navigate(`/event/${encodeURIComponent(data.slug)}?${next}`, {
          replace: true,
        });
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, navigate, searchParams, i18n]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 gap-3">
        <h1 className="text-xl font-extrabold text-ink-strong">
          {t('apply_link.not_found_title')}
        </h1>
        <p className="text-sm text-ink max-w-sm">
          {t('apply_link.not_found_body')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/listings')}
          className="mt-2 px-4 py-2.5 rounded-lg bg-ink text-white text-sm font-bold hover:bg-ink-black"
        >
          {t('apply_link.browse_events')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <PuffLoader color="#FFBD59" size={48} />
    </div>
  );
};

export default ApplyRedirect;
